<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { writable } from 'svelte/store';
  import { userRole, isReadOnly } from '$stores/sessionStore';

  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  const dispatch = createEventDispatcher();

  export let device = null;
  export let readingMetrics = [];
  export let deviceReadingTypes = [];
  export let displayUnits = [];
  export let title = "Device";
  export let onClose = () => {};
  export let onSave = () => {};

  let isLoading = false;
  let errorMessage = '';
  // Create a writable store for better reactivity
  let formData = writable({});
  
  // For direct access in the component
  let formDevice = {};
  // Subscribe to the store to keep formDevice in sync
  const unsubscribe = formData.subscribe(value => {
    formDevice = value;
  });
  
  // Additional lookup data
  let loadingLookups = false;
  let readingTypeOptions = [];
  let readingPurposeOptions = [];
  let displayUnitOptions = [];

  // Clone the device to avoid modifying the original
  onMount(async () => {
    // Initialize the store with the provided device data
    formData.set({ 
      ...device,
      deviceReadingTypeGUID: device?.deviceReadingTypeGUID || '',
      deviceReadingPurposeGUID: device?.deviceReadingPurposeGUID || '',
      defaultReadingDurationDisplayUnitGUID: device?.defaultReadingDurationDisplayUnitGUID || '',
      defaultReadingFrequencyDisplayUnitGUID: device?.defaultReadingFrequencyDisplayUnitGUID || ''
    });
    
    await loadLookupData();
    
    return () => {
      // Clean up subscription when component is destroyed
      unsubscribe();
    };
  });
  
  // Function to load all lookup data
  async function loadLookupData() {
    try {
      loadingLookups = true;
      
      // We're already receiving these from props, but let's check if they need to be loaded
      let needToLoadReadingTypes = deviceReadingTypes.length === 0;
      let needToLoadDisplayUnits = displayUnits.length === 0;
      let needToLoadReadingMetrics = readingMetrics.length === 0;
      
      if (needToLoadReadingTypes) {
        const drtRes = await fetch(`${API_BASE_URL}/lookups/DRT`);
        
        if (drtRes.ok) {
          const drtData = await drtRes.json();
          deviceReadingTypes = drtData.data || [];
        }
      }
      
      if (needToLoadDisplayUnits) {
        const unitsRes = await fetch(`${API_BASE_URL}/lookups/DURDISPUNT`);
        
        if (unitsRes.ok) {
          const unitsData = await unitsRes.json();
          displayUnits = unitsData.data || [];
        }
      }
      
      if (needToLoadReadingMetrics) {
        const metricsRes = await fetch(`${API_BASE_URL}/devices/readingmetrics?active=true`);
        
        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          readingMetrics = metricsData.data || [];
        }
      }
      
      // Based on the data inspection, map lookup data to options with correct ID fields
      // For reading types, it could be lookupGUID instead of _id - try all possible fields
      readingTypeOptions = deviceReadingTypes.map(type => {
        // Try every possible field that might contain the ID
        const id = type._id || type.lookupGUID || type.id || '';
        return {
          value: id,
          label: type.label || '',
          code: type.code || ''
        };
      });
      
      // Reading metrics seems to work correctly with _id
      readingPurposeOptions = readingMetrics.map(metric => ({
        value: metric._id || '',
        label: metric.name || '',
        code: metric.code || ''
      }));
      
      // For display units, like reading types, try all possible ID fields
      displayUnitOptions = displayUnits.map(unit => {
        // Try every possible field that might contain the ID
        const id = unit._id || unit.lookupGUID || unit.id || '';
        return {
          value: id,
          label: unit.label || '',
          code: unit.code || ''
        };
      });
      
    } catch (error) {
      console.error('Error loading lookup data:', error);
      errorMessage = 'Failed to load reference data: ' + (error.message || 'Unknown error');
    } finally {
      loadingLookups = false;
    }
  }

  function handleClose() {
    onClose();
  }
  
  // Validate form fields
  function validateForm() {
    if (!formDevice.name || !formDevice.code) {
      errorMessage = 'Name and code are required fields';
      return false;
    }
    
    // Ensure numeric fields are numbers or set to defaults
    const numericFields = [
      'defaultReadingDuration',
      'defaultReadingFrequency',
      'defaultReadingCount',
      'defaultReadingThreshold'
    ];
    
    numericFields.forEach(field => {
      // If empty or not set, default to 0
      if (formDevice[field] === undefined || formDevice[field] === null || formDevice[field] === '') {
        formData.update(data => ({ ...data, [field]: 0 }));
      } else if (isNaN(parseFloat(formDevice[field]))) {
        formData.update(data => ({ ...data, [field]: 0 }));
      } else {
        // Ensure it's a number
        const parsedValue = parseFloat(formDevice[field]);
        formData.update(data => ({ ...data, [field]: parsedValue }));
      }
    });
    
    errorMessage = '';
    return true;
  }

  async function handleSave() {
    try {
      if (!validateForm()) {
        return;
      }
      
      isLoading = true;
      await onSave(formDevice);
      handleClose();
    } catch (error) {
      errorMessage = error.message || 'Failed to save device';
      console.error('Error saving device:', error);
    } finally {
      isLoading = false;
    }
  }
  
  // Function to handle numeric inputs
  function handleNumericInput(event, field) {
    const value = event.target.value;
    if (value === '' || value === null) {
      formData.update(data => ({ ...data, [field]: '' }));
    } else {
      const parsed = parseFloat(value);
      formData.update(data => ({ ...data, [field]: isNaN(parsed) ? 0 : parsed }));
    }
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div 
  class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
  transition:fade={{ duration: 200 }}
>
  <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={handleClose}></div>
  
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div 
    class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60 overflow-y-auto max-h-[90vh]"
    on:click|stopPropagation
    transition:scale={{ duration: 300, start: 0.95 }}
  >
    <header class="flex justify-between items-start mb-4 text-gray-900 dark:text-gray-100">
      <h2 class="text-xl font-bold">{title}</h2>
      <button 
        class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" 
        on:click={handleClose}
      >&times;</button>
    </header>

    {#if errorMessage}
      <div class="mb-4 p-2 bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded">
        {errorMessage}
      </div>
    {/if}
    
    {#if loadingLookups}
      <div class="mb-4 text-center text-gray-600 dark:text-gray-400">
        Loading reference data...
      </div>
    {:else}
      <div class="flex flex-col gap-4 mb-4">
        <!-- Row 1: Name, Code, Status -->
        <div class="flex gap-3">
          <div class="w-3/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="name">
              Name*
            </label>
            <input
              type="text"
              id="name"
              bind:value={$formData.name}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="code">
              Code*
            </label>
            <input
              type="text"
              id="code"
              bind:value={$formData.code}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="active">
              Status
            </label>
            <select
              id="active"
              bind:value={$formData.active}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
          </div>
        </div>
        
        <!-- Row 2: Manufacturer, Model, Technology -->
        <div class="flex gap-3">
          <div class="w-2/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="manufacturer">
              Manufacturer
            </label>
            <input
              type="text"
              id="manufacturer"
              bind:value={$formData.manufacturer}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          
          <div class="w-2/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="model">
              Model
            </label>
            <input
              type="text"
              id="model"
              bind:value={$formData.model}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="technology">
              Technology
            </label>
            <select
              id="technology"
              bind:value={$formData.technology}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Technology</option>
              <option value="Bluetooth">Bluetooth</option>
              <option value="USB">USB</option>
              <option value="API">API</option>
            </select>
          </div>
        </div>
        
        <!-- Row 3: Device Reading Type, Reading Purpose, Default Reading Threshold -->
        <div class="flex gap-3">
          <div class="w-2/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="deviceReadingTypeGUID">
              Type
            </label>
            <select
              id="deviceReadingTypeGUID"
              bind:value={$formData.deviceReadingTypeGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Reading Type</option>
              {#each readingTypeOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>
          
          <div class="w-2/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="deviceReadingPurposeGUID">
              Purpose
            </label>
            <select
              id="deviceReadingPurposeGUID"
              bind:value={$formData.deviceReadingPurposeGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Reading Purpose</option>
              {#each readingPurposeOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingThreshold">
              Threshold
            </label>
            <input
              type="number"
              id="defaultReadingThreshold"
              value={$formData.defaultReadingThreshold || 0}
              on:input={(e) => handleNumericInput(e, 'defaultReadingThreshold')}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        
        <!-- Row 4: Default Reading Duration, Duration Display Unit, Default Reading Frequency, Frequency Display Unit -->
        <div class="flex gap-3 mt-4">
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingDuration">
              Duration
            </label>
            <input
              type="number"
              id="defaultReadingDuration"
              value={$formData.defaultReadingDuration || 0}
              on:input={(e) => handleNumericInput(e, 'defaultReadingDuration')}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingDurationDisplayUnitGUID">
              Unit
            </label>
            <select
              id="defaultReadingDurationDisplayUnitGUID"
              bind:value={$formData.defaultReadingDurationDisplayUnitGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Unit</option>
              {#each displayUnitOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingFrequency">
              Frequency
            </label>
            <input
              type="number"
              id="defaultReadingFrequency"
              value={$formData.defaultReadingFrequency || 0}
              on:input={(e) => handleNumericInput(e, 'defaultReadingFrequency')}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          
          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingFrequencyDisplayUnitGUID">
              Unit
            </label>
            <select
              id="defaultReadingFrequencyDisplayUnitGUID"
              bind:value={$formData.defaultReadingFrequencyDisplayUnitGUID}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value="">Select Unit</option>
              {#each displayUnitOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>

          <div class="w-1/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="defaultReadingCount">
              Reading Count
            </label>
            <input
              type="number"
              id="defaultReadingCount"
              value={$formData.defaultReadingCount || 0}
              on:input={(e) => handleNumericInput(e, 'defaultReadingCount')}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        
        <!-- Row 5: Default Reading Count, Image URL -->
        <div class="flex gap-3 mt-4">
          <div class="w-full">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="url">
              Image URL
            </label>
            <input
              type="text"
              id="url"
              bind:value={$formData.url}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        
        <!-- Row 6: Description -->
        <div class="w-full mt-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" for="description">
            Description
          </label>
          <textarea
            id="description"
            bind:value={$formData.description}
            class="border p-2 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            rows="3"
          ></textarea>
        </div>
      </div>
    {/if}

    <div class="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700">
      <button 
        on:click={handleClose} 
        class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700 disabled:opacity-50"
        disabled={isLoading || loadingLookups}
      >
        Cancel
      </button>
      <button 
        on:click={handleSave} 
        class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
        disabled={isReadOnly() || isLoading || loadingLookups}
      >
        {isLoading ? 'Saving...' : 'Save'}
      </button>
    </div>
  </div>
</div> 